import { router } from "@inertiajs/react"
import { Globe, Plug } from "lucide-react"
import { type FormEvent, type ReactNode, useState } from "react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { ArtifactBlock } from "@/components/artifact-block"
import { FormField } from "@/components/form-field"
import { ApiKeyCredentialsForm } from "@/components/integrations/api-key-credentials-form"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { INTEGRATION_REGISTRY } from "@/lib/integration-registry"
import { settingsIntegrationCredentialConnectPath } from "@/routes"

import type {
  LeadResponderSetupData,
  LeadResponderSetupStepProps,
  LeadSource,
} from "./types"

// ---------------------------------------------------------------------------
// Mock data. In the real build these come from connected accounts via deferred
// props (the receptionist's calendar pickers are the pattern to follow): the
// CRM segments arrive from the CRM API once the OAuth handshake completes.
//
// The snippet key / webhook id are derived from the same per-agent lead key,
// generated server-side.
// ---------------------------------------------------------------------------

const LEAD_KEY = "ld_8f2a9c1b4e"

const WEB_SNIPPET = `<script src="https://app.imperative.so/lead.js"
  data-key="${LEAD_KEY}"></script>`

const WEBHOOK_URL = `https://app.imperative.so/hooks/${LEAD_KEY}`

// The body Webhooks::LeadsController reads: top-level name / email / phone, as
// JSON or form fields. Email or phone is required (IngestLead rejects a lead
// with neither).
const WEBHOOK_EXAMPLE_BODY = `{
  "name": "Jane Smith",
  "email": "jane@example.com",
  "phone": "+15555550123"
}`

// IMP-417: the CRM picker is server-driven. The Lead Responder backend
// (Agents::LeadResponder::CrmProviders) is the single source of truth for which
// CRMs work end-to-end; it sends the supported picker ids as
// crm.available_providers, and we render only those — an unsupported CRM is
// never advertised (no drift, no "coming soon" dead ends). Label + logo come
// from the shared INTEGRATION_REGISTRY (keyed by integration kind), the same
// source the connect dialog uses, so a provider needs exactly one catalog entry.

// IMP-347: HubSpot's picker id ("hubspot") connects under the integration kind
// "hubspot_crm"; every other CRM is 1:1. Kept in one place (the server keeps its
// own map in Agents::LeadResponder::CrmProviders).
const CRM_KIND_BY_ID: Record<string, string> = { hubspot: "hubspot_crm" }
function crmKind(providerId: string): string {
  return CRM_KIND_BY_ID[providerId] ?? providerId
}

// Design-preview fallback only: real setup always receives
// crm.available_providers from the server. Mirrors today's supported set.
const FALLBACK_CRM_IDS = ["hubspot", "pipedrive"]

interface CrmPickerEntry {
  id: string
  label: string
  icon: ReactNode
}

// Resolve a supported picker id to its label + logo from the integration
// registry. Falls back to the raw id if a registry entry is somehow missing (the
// backend wouldn't return an id it can't connect, so this is defensive only).
function crmEntry(id: string): CrmPickerEntry {
  const registry = INTEGRATION_REGISTRY[crmKind(id)]
  return { id, label: registry?.label ?? id, icon: registry?.icon }
}

// IMP-419: the CRM source is labeled in each provider's own terms. Pipedrive has
// no "lists/segments" — its analog is a saved Filter on Persons — so the picker
// must not call it a "list or segment".
interface CrmSourceTerms {
  label: string // field label, e.g. "Filter"
  placeholder: string // select placeholder, e.g. "Choose a filter"
  empty: string // empty-state, e.g. "No filters found"
  none: string // IMP-434: the in-dropdown clear option, e.g. "No filter"
}
const CRM_SOURCE_TERMS: Record<string, CrmSourceTerms> = {
  pipedrive: {
    label: "Filter",
    placeholder: "Choose a filter",
    empty: "No filters found",
    none: "No filter",
  },
}
const DEFAULT_CRM_SOURCE_TERMS: CrmSourceTerms = {
  label: "List or segment",
  placeholder: "Choose a list or segment",
  empty: "No lists found",
  none: "No list",
}
function crmTerms(provider: string): CrmSourceTerms {
  return CRM_SOURCE_TERMS[provider] ?? DEFAULT_CRM_SOURCE_TERMS
}

// IMP-434: the filter <Select>'s clear option. Radix forbids an empty-string
// item value, so map this sentinel to "" (no filter) at the boundary.
const NO_FILTER = "__none__"

// One provider in the picker grid. Same card shape as the calendar-integration
// step (logo top-left, label below), minus the per-card connect button — picking
// the card connects it. Only supported CRMs are ever rendered here.
function CrmProviderButton({
  entry,
  onSelect,
}: {
  entry: CrmPickerEntry
  onSelect: (id: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(entry.id)}
      className="border-border rounded-md border p-3 text-left transition-colors hover:border-blue-200"
    >
      <div className="mb-4 flex items-start">
        <span className="flex size-8 shrink-0 items-center">{entry.icon}</span>
      </div>
      <p className="text-sm font-medium">{entry.label}</p>
    </button>
  )
}

// IMP-346: the per-agent intake artifacts + which push sources have received a
// lead, supplied by the server. Optional so the /design preview still renders
// with the placeholder constants below. (The forward-to email artifact was
// pulled pending IMP-397.)
export interface LeadIntake {
  snippet: string
  webhook_url: string
  received_sources: string[]
}

// IMP-347: account-level CRM connection state + the lists to pick from, for the
// CRM this agent watches. IMP-417: `available_providers` is the server's set of
// picker ids that work end-to-end (connect + list + pull); the picker renders
// only these, so an unsupported CRM is never shown.
export interface CrmSource {
  connected: boolean
  provider: string
  available_providers?: string[]
  lists?: { id: string; name: string }[]
}

interface SourcesStepProps extends LeadResponderSetupStepProps {
  leadIntake?: LeadIntake
  crm?: CrmSource
}

// TEMP: the CRM lead source is hidden from setup + edit for now — flip this back
// to true to bring it back. All the CRM machinery below stays intact.
const SHOW_CRM_SOURCE = false

export function SourcesStep({
  stepNumber,
  totalSteps,
  data,
  onUpdate,
  onNext,
  leadIntake,
  crm,
}: SourcesStepProps) {
  return (
    <WizardLayout footer={<Button onClick={onNext}>Continue</Button>}>
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Connect your lead sources"
        description="Set up at least one place your leads come in. You can add more anytime."
      />
      <SourcesFields
        data={data}
        onUpdate={onUpdate}
        leadIntake={leadIntake}
        crm={crm}
      />
    </WizardLayout>
  )
}

// IMP-371: the source-configuration body — CRM connection + the three push
// sources. Extracted so the same controls power both the setup wizard's Sources
// step and the dashboard edit drawer's Sources tab (an owner can change sources
// after going live).
export function SourcesFields({
  data,
  onUpdate,
  leadIntake,
  crm,
}: {
  data: LeadResponderSetupData
  onUpdate: (patch: Partial<LeadResponderSetupData>) => void
  leadIntake?: LeadIntake
  crm?: CrmSource
}) {
  // The picker id currently being connected (its credentials dialog is open), or
  // null. Any supported CRM connects inline this way.
  const [connectingCrm, setConnectingCrm] = useState<string | null>(null)
  // IMP-434: "Change CRM" forces the provider picker back even while an
  // account-level CRM is still connected, so this agent can be pointed at a
  // different provider. Purely local (this agent's source selection).
  const [changingCrm, setChangingCrm] = useState(false)

  // Real per-agent artifacts when present, else the design-preview placeholders.
  const webSnippet = leadIntake?.snippet ?? WEB_SNIPPET
  const webhookUrl = leadIntake?.webhook_url ?? WEBHOOK_URL

  // IMP-347: connecting a CRM is an account-level action (reusable across
  // agents), but it happens inline here — a credentials dialog that stays on this
  // page and returns with the connection live + lists populated, so the business
  // can immediately pick the list to watch.
  // IMP-417: the picker lists only the server's supported CRMs (falling back to
  // the known set in the design preview, which has no server props).
  const crmConnected = crm?.connected ?? false
  const availableCrmIds = crm?.available_providers ?? FALLBACK_CRM_IDS
  const crmLists = (crm?.lists ?? []).map((l) => ({
    value: l.id,
    label: l.name,
  }))
  // Picking a provider from the grid: if the account already has that CRM
  // connected, just point this agent at it (no re-entering credentials);
  // otherwise open its connect dialog.
  const selectCrm = (provider: string) => {
    if (crmConnected && crm?.provider === provider) {
      onUpdate({ crm_provider: provider })
      setChangingCrm(false)
    } else {
      setConnectingCrm(provider)
    }
  }

  return (
    <>
      {/* Connect your CRM — the primary source, where most SMBs start. */}
      {SHOW_CRM_SOURCE && (
        <FormField label="Connect your CRM">
          <CrmCard
            provider={crm?.provider ?? data.crm_provider}
            connected={crmConnected && !changingCrm}
            segment={data.crm_scope_value}
            segments={crmLists}
            availableProviders={availableCrmIds}
            onConnect={selectCrm}
            onSegmentChange={(v) =>
              onUpdate({
                sources: setSource(data.sources, "crm", v !== ""),
                crm_provider: crm?.provider ?? "hubspot",
                crm_scope_value: v,
              })
            }
            // IMP-434: "Change CRM" clears THIS agent's source selection and
            // returns to the picker. It never touches the account-level
            // connection (that lives in Settings → Integrations).
            onChangeCrm={() => {
              onUpdate({
                sources: data.sources.filter((x) => x !== "crm"),
                crm_provider: "",
                crm_scope_value: "",
              })
              setChangingCrm(true)
            }}
          />
        </FormField>
      )}

      {/* Push sources — point existing tools at us. Collapsed rows so the page
          isn't a wall of snippets; each reveals its artifact and a status the
          user can verify. */}
      <FormField label={SHOW_CRM_SOURCE ? "Or add another source" : "Sources"}>
        {/* FormField gives one child slot, so the rows carry their own gap. */}
        <div className="space-y-2">
          <PushSourceRow
            icon={
              <Globe className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
            }
            title="Website forms"
            instructions="Paste this just before the closing </body> tag on any page with a form."
            artifact={webSnippet}
          />

          <PushSourceRow
            icon={
              <Plug className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
            }
            title="Custom"
            instructions="POST leads to this webhook URL from Zapier or any other tool."
            artifact={webhookUrl}
          >
            <p className="text-muted-foreground text-sm">
              Send <Code>name</Code>, <Code>email</Code>, and <Code>phone</Code>{" "}
              as JSON or form fields. Each lead needs an email or phone number.
            </p>
            <ArtifactBlock artifact={WEBHOOK_EXAMPLE_BODY} />
          </PushSourceRow>
        </div>
      </FormField>

      {SHOW_CRM_SOURCE && (
        <CrmCredentialDialog
          provider={connectingCrm}
          onClose={() => {
            setConnectingCrm(null)
            // Whether they connected a new CRM or backed out, leave "changing"
            // mode — the connected card reflects the current selection again.
            setChangingCrm(false)
          }}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// CRM connect — an inline credentials dialog (IMP-347), generic over any
// supported CRM. Renders the provider's fields (from the integration registry)
// and POSTs them to the same credential-connect endpoint the Settings →
// Integrations page uses, with return_to set to this page, so the connection
// lands live here (crm props reload with the lists) instead of stranding the
// user on the settings page.
// ---------------------------------------------------------------------------

function CrmCredentialDialog({
  provider,
  onClose,
}: {
  // The picker id being connected (e.g. "hubspot", "pipedrive"), or null.
  provider: string | null
  onClose: () => void
}) {
  const kind = provider ? crmKind(provider) : ""
  const registry = provider ? INTEGRATION_REGISTRY[kind] : undefined
  const fields = registry?.credentials ?? []
  const label = registry?.label ?? "CRM"

  const [values, setValues] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const filled =
    fields.length > 0 &&
    fields.every((f) => (values[f.key] ?? "").trim() !== "")

  function reset() {
    setValues({})
    setError(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setError(null)
    router.post(
      settingsIntegrationCredentialConnectPath(kind),
      {
        ...values,
        return_to:
          typeof window === "undefined" ? "" : window.location.pathname,
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          reset()
          onClose()
        },
        onError: () =>
          setError(
            `Couldn't connect ${label}. Check the details and try again.`,
          ),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Dialog
      open={provider !== null}
      onOpenChange={(v) => {
        if (!v) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Connect {label}</DialogTitle>
            <DialogDescription>
              Enter your {label} credentials so the agent can watch a list and
              work new contacts as they come in.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {fields.length > 0 && (
              <ApiKeyCredentialsForm
                fields={fields}
                values={values}
                onChange={setValues}
                idPrefix={`crm-${kind}`}
              />
            )}
            {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!filled || processing}
              loading={processing}
            >
              {processing ? "Connecting…" : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// CRM source card. Before connecting, shows a logo grid of the CRMs the backend
// actually supports end-to-end (IMP-417: server-driven, so the picker can't
// advertise a CRM we don't implement). Once a provider is picked the picker
// collapses to a compact connected card where the list/segment is scoped.
// Enabled (counts as a source) once a segment is chosen.
// ---------------------------------------------------------------------------

function CrmCard({
  provider,
  connected,
  segment,
  segments,
  availableProviders,
  onConnect,
  onSegmentChange,
  onChangeCrm,
}: {
  provider: string
  // IMP-347: account-level connection state (the CRM connected under Settings →
  // Integrations), not merely whether a provider was clicked.
  connected: boolean
  segment: string
  segments: { value: string; label: string }[]
  // IMP-417: the server's supported picker ids — the only CRMs shown.
  availableProviders: string[]
  onConnect: (id: string) => void
  onSegmentChange: (segment: string) => void
  // IMP-434: reset this agent's CRM selection and return to the picker.
  onChangeCrm: () => void
}) {
  // Pre-connect: a logo grid of exactly the supported CRMs.
  if (!connected) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {availableProviders.map((id) => (
          <CrmProviderButton
            key={id}
            entry={crmEntry(id)}
            onSelect={onConnect}
          />
        ))}
      </div>
    )
  }

  // Connected: compact card, scope the list/segment (Pipedrive: filter) inline.
  const entry = crmEntry(provider || "hubspot")
  const terms = crmTerms(provider || "hubspot")
  return (
    <div className="rounded-lg border bg-white dark:bg-transparent">
      <div className="flex items-center gap-3 p-3">
        <span className="flex size-8 shrink-0 items-center">{entry.icon}</span>
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium">{entry.label}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onChangeCrm}>
          Change CRM
        </Button>
      </div>

      <div className="space-y-1.5 px-3 pb-3">
        <Label>{terms.label}</Label>
        <Select
          value={segment || NO_FILTER}
          onValueChange={(v) => onSegmentChange(v === NO_FILTER ? "" : v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={terms.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {/* IMP-434: clearing the filter lives in the dropdown, not a
                separate top-right button. */}
            <SelectItem value={NO_FILTER}>{terms.none}</SelectItem>
            {segments.length === 0 ? (
              <div className="text-muted-foreground px-2 py-1.5 text-sm">
                {terms.empty}
              </div>
            ) : (
              segments.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Push source — instructions + a copyable artifact (no expand).
// ---------------------------------------------------------------------------

function PushSourceRow({
  icon,
  title,
  instructions,
  artifact,
  children,
}: {
  icon: ReactNode
  title: string
  instructions: string
  artifact: string
  // Extra guidance below the artifact (e.g. the webhook's expected body).
  children?: ReactNode
}) {
  return (
    <div className="space-y-2 rounded-lg border bg-white p-3 dark:bg-transparent">
      {/* The icon keeps its own column so the title and instructions share a
          left edge; its mt-0.5 is optical alignment to the first text line,
          not label/sublabel spacing. */}
      <div className="flex items-start gap-2">
        {icon}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-muted-foreground mt-1 text-sm">{instructions}</p>
        </div>
      </div>

      <ArtifactBlock artifact={artifact} />
      {children}
    </div>
  )
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">
      {children}
    </code>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function addSource(sources: LeadSource[], s: LeadSource): LeadSource[] {
  return sources.includes(s) ? sources : [...sources, s]
}

// Add or remove a source from the connected list based on whether it's now
// active (e.g. the CRM is "on" only once a list/segment is selected).
function setSource(
  sources: LeadSource[],
  s: LeadSource,
  on: boolean,
): LeadSource[] {
  if (on) return addSource(sources, s)
  return sources.filter((x) => x !== s)
}
